import React, { useRef, useState } from 'react';
import {
  StaysSyncError,
  SyncDestination,
  SyncExport,
  createExportLink,
  revokeExportLink,
} from '../../../../api/stays-sync-client';
import { AppButton } from '../../../button/app-button';
import { HelperText } from '../../../form-kit';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { Collapse } from '../../../../motion';
import toast from '../../../../utils/toast';
import { DESTINATIONS, destinationHint, destinationName, exportState } from '../../../../utils/stays-sync';
import { ExportReveal } from './export-reveal';
import { Actions, Card, H3, Muted, RowItem, RowLine, RowMain, RowTitle, Rows } from './sync.styled';

/**
 * EXPORT TO BOOKING SITES (plan S5, screens §1 "Export card"): one row per
 * destination — Airbnb, Booking.com, Vrbo, a personal calendar — each with
 * its own link, so every booking site sees the OTHER sites' stays and the
 * closed dates as unavailable. A new or replaced link is revealed once
 * (ExportReveal); replacing or turning one off is confirmed first, because the
 * old link stops working at once.
 */

export interface ExportCardProps {
  listingId: string;
  exports: SyncExport[];
  canManage: boolean;
  now: Date;
  /** After any change: the page reloads its data. */
  onChanged: () => void;
}

type Pending = { kind: 'replace' | 'off'; destination: SyncDestination; exportId: string } | null;

export const ExportCard: React.FC<ExportCardProps> = ({ listingId, exports, canManage, now, onChanged }) => {
  const [revealed, setRevealed] = useState<{ destination: SyncDestination; url: string } | null>(null);
  const [busy, setBusy] = useState<SyncDestination | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const guard = useRef(false);

  const byDest = (d: SyncDestination) => exports.find(x => x.destination === d) || null;

  const make = async (destination: SyncDestination) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(destination);
    setRevealed(null);
    try {
      const out = await createExportLink(listingId, destination);
      setRevealed({ destination, url: out.url });
      onChanged();
    } catch (err) {
      const x = err as StaysSyncError;
      toast.error(x && x.status > 0 && x.message ? x.message : 'The link could not be made. Please try again.');
    } finally {
      guard.current = false;
      setBusy(null);
    }
  };

  const turnOff = async (destination: SyncDestination, exportId: string) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(destination);
    try {
      await revokeExportLink(exportId);
      if (revealed && revealed.destination === destination) setRevealed(null);
      toast.success(`The ${destinationName(destination)} link is off`);
      onChanged();
    } catch (err) {
      const x = err as StaysSyncError;
      toast.error(x && x.status > 0 && x.message ? x.message : 'The link could not be turned off. Please try again.');
    } finally {
      guard.current = false;
      setBusy(null);
    }
  };

  const confirm = async () => {
    const p = pending;
    setPending(null);
    if (!p) return;
    if (p.kind === 'replace') await make(p.destination);
    else await turnOff(p.destination, p.exportId);
  };

  const pendingName = pending ? destinationName(pending.destination) : '';

  return (
    <Card aria-labelledby="sync-export-title" data-testid="sync-export-card">
      <H3 id="sync-export-title">Export to booking sites</H3>
      <HelperText>Give each booking site its own link, so it sees your other stays and closed dates as unavailable.</HelperText>
      <Rows>
        {DESTINATIONS.map(({ key, name }) => {
          const x = byDest(key);
          const hint = destinationHint(key);
          const open = !!revealed && revealed.destination === key;

          return (
            <RowItem key={key} data-testid={`sync-export-${key}`}>
              <RowMain>
                <RowTitle>{name}</RowTitle>
                <RowLine data-at={x && x.last_read_at ? x.last_read_at : undefined}>{exportState(x, now)}</RowLine>
                {hint ? <Muted>{hint}</Muted> : null}
                <Collapse open={open}>
                  {open && revealed ? (
                    <ExportReveal destination={key} url={revealed.url} onDone={() => setRevealed(null)} />
                  ) : null}
                </Collapse>
              </RowMain>
              {canManage ? (
                <Actions>
                  {x ? (
                    <>
                      <AppButton
                        variant="secondary"
                        size="compact"
                        type="button"
                        loading={busy === key}
                        loadingLabel="Working…"
                        disabled={!!busy && busy !== key}
                        aria-label={`Replace the ${name} link`}
                        onClick={() => setPending({ kind: 'replace', destination: key, exportId: x.id })}
                      >
                        Replace link
                      </AppButton>
                      <AppButton
                        variant="secondary"
                        size="compact"
                        type="button"
                        disabled={!!busy}
                        aria-label={`Turn off the ${name} link`}
                        onClick={() => setPending({ kind: 'off', destination: key, exportId: x.id })}
                      >
                        Turn off
                      </AppButton>
                    </>
                  ) : (
                    <AppButton
                      variant="primary"
                      size="compact"
                      type="button"
                      loading={busy === key}
                      loadingLabel="Making the link…"
                      disabled={!!busy && busy !== key}
                      aria-label={`Create the ${name} link`}
                      onClick={() => void make(key)}
                    >
                      Create link
                    </AppButton>
                  )}
                </Actions>
              ) : null}
            </RowItem>
          );
        })}
      </Rows>
      <ConfirmDialog
        open={!!pending && pending.kind === 'replace'}
        title={`Replace the ${pendingName} link?`}
        message={`The old link stops working at once. Paste the new one into ${pendingName}, or it will stop seeing your other stays.`}
        primaryLabel="Replace the link"
        onPrimary={confirm}
        dismissLabel="Keep the old link"
        onDismiss={() => setPending(null)}
      />
      <ConfirmDialog
        open={!!pending && pending.kind === 'off'}
        title={`Turn off the ${pendingName} link?`}
        message={`${pendingName} stops seeing this listing's calendar at once. You can make a new link later.`}
        primaryLabel="Turn off the link"
        primaryVariant="destructive"
        onPrimary={confirm}
        dismissLabel="Keep it"
        onDismiss={() => setPending(null)}
      />
    </Card>
  );
};
